// src/components/blog/features/AnimatedTableOfContents.tsx

'use client'

import { motion } from 'motion/react'
import { useEffect, useState, useRef, useCallback } from 'react'
import RiArrowUpSLine from '~/icons/remix/generated/RiArrowUpSLine'
import RiArrowDownSLine from '~/icons/remix/generated/RiArrowDownSLine'
import RiFileListLine from '~/icons/remix/generated/RiFileListLine'
import type { TOCItem } from '~/lib/blog/types/blog.types'
import { TableOfContentsList } from '~/components/blog/components/TableOfContentsList'
import { useTableOfContentsScrollSpy } from '~/components/blog/components/useTableOfContentsScrollSpy'

/**
 * Props for the AnimatedTableOfContents component
 *
 * @interface AnimatedTableOfContentsProps
 * @property {TOCItem[]} items - Array of TOC items to display
 * @property {number} navbarHeight - Height of fixed navbar elements in pixels, used for scroll offset calculations
 */
interface AnimatedTableOfContentsProps {
  /** Array of table of contents items to display */
  items: TOCItem[]
  /** Total height of fixed navbar elements in pixels, used for scroll offset calculations */
  navbarHeight: number
}

/**
 * Configuration for scroll offset calculation
 */
const SCROLL_CONFIG = {
  /** Additional buffer space below navbar when scrolling to headings */
  BUFFER_SPACE: 50,
  /** Scroll amount in pixels for up/down buttons */
  SCROLL_AMOUNT: 150,
  /** Threshold in pixels to show/hide scroll indicators */
  INDICATOR_THRESHOLD: 20,
} as const

/**
 * AnimatedTableOfContents - the interactive table of contents.
 *
 * Presentation (card, header, indented anchor list, active state) is rendered by
 * the shared {@link TableOfContentsList} base — the same one the static
 * `TableOfContentsServer` fallback uses. This component owns only the
 * interactivity: scroll-spy (`activeId` from {@link useTableOfContentsScrollSpy}),
 * smooth-scroll-to-heading with a fixed-navbar offset, auto-scrolling the active
 * row into view, and the overflow scroll-indicator buttons.
 *
 * @param {TOCItem[]} items - Array of TOC items to display
 * @param {number} navbarHeight - Height of fixed navbar elements (from useNavbarHeight)
 */
export function AnimatedTableOfContents({
  items,
  navbarHeight,
}: AnimatedTableOfContentsProps): JSX.Element {
  const [activeId, setActiveId] = useTableOfContentsScrollSpy(items)
  const [showScrollIndicators, setShowScrollIndicators] = useState({ top: false, bottom: true })
  const tocListRef = useRef<HTMLUListElement>(null)

  /**
   * Updates the visibility state of scroll indicators based on scroll position.
   */
  const updateScrollIndicators = useCallback((): void => {
    if (!tocListRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = tocListRef.current
    const isScrollable = scrollHeight > clientHeight

    setShowScrollIndicators({
      top: isScrollable && scrollTop > SCROLL_CONFIG.INDICATOR_THRESHOLD,
      bottom: isScrollable && scrollTop + clientHeight < scrollHeight - SCROLL_CONFIG.INDICATOR_THRESHOLD,
    })
  }, [])

  /**
   * Auto-scrolls the active TOC row into view within the (possibly overflowing)
   * TOC container whenever the active heading changes.
   */
  useEffect(() => {
    if (!activeId || !tocListRef.current) return

    const activeElement = tocListRef.current.querySelector(`[data-id="${activeId}"]`)
    if (!activeElement) return

    const containerRect = tocListRef.current.getBoundingClientRect()
    const activeRect = activeElement.getBoundingClientRect()
    if (activeRect.top < containerRect.top || activeRect.bottom > containerRect.bottom) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [activeId])

  /**
   * Keeps the scroll-indicator visibility in sync with the TOC container's scroll.
   */
  useEffect(() => {
    const list = tocListRef.current
    if (!list) return

    updateScrollIndicators()
    list.addEventListener('scroll', updateScrollIndicators)
    return () => list.removeEventListener('scroll', updateScrollIndicators)
  }, [items, updateScrollIndicators])

  /**
   * Handles scroll button clicks (up/down arrows).
   */
  const handleScroll = (direction: 'up' | 'down'): void => {
    if (!tocListRef.current) return

    const currentScroll = tocListRef.current.scrollTop
    const scrollAmount = direction === 'up' ? -SCROLL_CONFIG.SCROLL_AMOUNT : SCROLL_CONFIG.SCROLL_AMOUNT

    tocListRef.current.scrollTo({ top: currentScroll + scrollAmount, behavior: 'smooth' })
  }

  /**
   * Smoothly scrolls to the clicked heading, offsetting for the fixed navbar.
   */
  const handleTocItemClick = (e: React.MouseEvent, id: string): void => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (!element) return

    const headerOffset = navbarHeight + SCROLL_CONFIG.BUFFER_SPACE
    const y = element.getBoundingClientRect().top + window.scrollY - headerOffset
    window.scrollTo({ top: y, behavior: 'smooth' })

    // Set active immediately: clicking an *earlier* heading scrolls it above the
    // scroll-spy's active band, so the observer may never fire for it — without
    // this the previously-active row would stay highlighted.
    setActiveId(id)
  }

  return (
    <TableOfContentsList
      items={items}
      activeId={activeId}
      className="relative"
      label="On this page"
      icon={<RiFileListLine className="mr-2 h-4 w-4 text-indigo-500" aria-hidden="true" />}
      onItemClick={handleTocItemClick}
      listRef={tocListRef}
      listClassName="max-h-[calc(100dvh-300px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
    >
      {/* Scroll up indicator */}
      {showScrollIndicators.top && (
        <div className="absolute left-0 right-0 top-12 z-10 flex justify-center pointer-events-none">
          <motion.button
            onClick={() => handleScroll('up')}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="w-7 h-7 rounded-full bg-white dark:bg-gray-700 shadow-md flex items-center justify-center pointer-events-auto"
            aria-label="Scroll up"
          >
            <RiArrowUpSLine className="h-4 w-4 text-gray-500 dark:text-gray-300" aria-hidden="true" />
          </motion.button>
        </div>
      )}

      {/* Scroll down indicator */}
      {showScrollIndicators.bottom && (
        <div className="absolute left-0 right-0 bottom-2 z-10 flex justify-center pointer-events-none">
          <motion.button
            onClick={() => handleScroll('down')}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="w-7 h-7 rounded-full bg-white dark:bg-gray-700 shadow-md flex items-center justify-center pointer-events-auto"
            aria-label="Scroll down"
          >
            <RiArrowDownSLine className="h-4 w-4 text-gray-500 dark:text-gray-300" aria-hidden="true" />
          </motion.button>
        </div>
      )}
    </TableOfContentsList>
  )
}
